import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, MapPin } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default recurring events
const recurringEvents = [
  {
    id: 'sabbath',
    title: "Sabbath Service",
    description: 'Join us for our weekly Sabbath worship service — in person or via YouTube live stream. We integrate teachings from the Lost Books into our service.',
    day: 'Saturday',
    dayNum: 6,
    time: '9:45 AM CST',
    location: 'In-Person & YouTube',
    address: '10303 Northwest Freeway, Suite 263, Houston, TX 77092',
    recurring: true,
    primary: true
  },
  {
    id: 'bidds',
    title: "Prayer & B.I.D.D.",
    description: 'Break It Down Discussions - Virtual study of the Lost Books of the Bible via Zoom.',
    day: 'Wednesday',
    dayNum: 3,
    time: '7:00 PM CST',
    location: 'Virtual (Zoom)',
    address: null,
    recurring: true,
    primary: false
  }
];

export const Events = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get events for selected date
  const getEventsForDate = (date) => {
    const dateEvents = events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });

    // Add recurring events based on day of week
    recurringEvents.forEach(event => {
      if (date.getDay() === event.dayNum) {
        dateEvents.unshift({
          ...event,
          date: date.toISOString()
        });
      }
    });

    return dateEvents;
  };

  const selectedEvents = getEventsForDate(selectedDate);

  // Get all dates that have events
  const eventDates = events.map(event => new Date(event.date));
  
  // Add all Saturdays and Wednesdays for the next 3 months for recurring services
  const today = new Date();
  for (let i = 0; i < 90; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    // Saturday (6) or Wednesday (3)
    if (date.getDay() === 6 || date.getDay() === 3) {
      eventDates.push(date);
    }
  }

  return (
    <div data-testid="events-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Stay Connected</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Events <span className="text-gold">Calendar</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              View our upcoming events, services, and community gatherings. Join us as we grow together in faith.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <div className="card-dark p-4" data-testid="calendar-container">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md"
                  modifiers={{
                    hasEvent: eventDates
                  }}
                  modifiersStyles={{
                    hasEvent: {
                      backgroundColor: 'rgba(212, 175, 55, 0.2)',
                      borderRadius: '50%'
                    }
                  }}
                />
              </div>

              {/* Legend */}
              <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-gold/20" />
                  <span>Has Events</span>
                </div>
              </div>
            </motion.div>

            {/* Events List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-cinzel text-2xl text-gold">
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </h2>
              </div>

              {loading ? (
                <div className="card-dark text-center py-12">
                  <p className="text-muted-foreground">Loading events...</p>
                </div>
              ) : selectedEvents.length > 0 ? (
                <div className="space-y-4">
                  {selectedEvents.map((event, index) => (
                    <motion.div
                      key={event.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`card-dark ${event.recurring ? 'gold-glow' : ''}`}
                      data-testid={`event-${event.id || index}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          {event.recurring && (
                            <span className="inline-block bg-gold/20 text-gold text-xs px-2 py-1 rounded-full mb-2">
                              Weekly Service
                            </span>
                          )}
                          <h3 className="font-cinzel text-xl text-foreground mb-2">{event.title}</h3>
                          <p className="text-muted-foreground text-sm mb-4">{event.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Clock size={16} className="text-gold" />
                              <span>{event.time}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin size={16} className="text-gold" />
                              <span>{event.location}</span>
                            </div>
                          </div>
                        </div>
                        {event.recurring && (
                          <a
                            href="https://zoom.us"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary text-sm py-2 px-4 whitespace-nowrap"
                          >
                            Join on Zoom
                          </a>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="card-dark text-center py-12">
                  <CalendarIcon className="text-muted-foreground mx-auto mb-4" size={48} />
                  <p className="text-muted-foreground">No events scheduled for this day</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Join us every Wednesday at 7 PM for our weekly B.I.D.D's Discussion
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recurring Events */}
      <section className="py-24 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-cinzel text-3xl text-gold mb-4">Regular Schedule</h2>
            <p className="text-muted-foreground">
              Our recurring services and meetings
            </p>
          </motion.div>

          <div className="space-y-6">
            {/* Sabbath Service */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-dark gold-glow"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-start gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-cinzel text-gold">SAT</div>
                    <div className="text-4xl font-bold text-foreground">9:45</div>
                    <div className="text-muted-foreground text-sm">AM CST</div>
                  </div>
                  <div>
                    <span className="inline-block bg-gold/20 text-gold text-xs px-2 py-1 rounded-full mb-2">
                      In-Person & YouTube
                    </span>
                    <h3 className="font-cinzel text-xl text-foreground mb-2">Sabbath Service</h3>
                    <p className="text-muted-foreground mb-2">
                      Weekly Sabbath worship — in person or via YouTube live stream.
                    </p>
                    <p className="text-sm text-muted-foreground flex items-start gap-2">
                      <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
                      <span>10303 Northwest Freeway, Suite 263, Houston, TX 77092</span>
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://www.youtube.com/channel/UCdJtc_ATtcQV6BKByvucFQA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary whitespace-nowrap text-sm"
                  >
                    Watch on YouTube
                  </a>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=10303+Northwest+Freeway+Suite+263+Houston+TX+77092"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary whitespace-nowrap text-sm"
                  >
                    Directions
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Wednesday Prayer & B.I.D.D. */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-dark"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-cinzel text-gold">WED</div>
                    <div className="text-4xl font-bold text-foreground">7:00</div>
                    <div className="text-muted-foreground text-sm">PM CST</div>
                  </div>
                  <div>
                    <span className="inline-block bg-deep-red/20 text-deep-red text-xs px-2 py-1 rounded-full mb-2">
                      Virtual (Zoom)
                    </span>
                    <h3 className="font-cinzel text-xl text-foreground mb-2">Prayer & B.I.D.D.</h3>
                    <p className="text-muted-foreground">
                      Virtual study of the Lost Books of the Bible, exploring scripture in its original context.
                    </p>
                  </div>
                </div>
                <a
                  href="https://us06web.zoom.us/j/94303159703?pwd=d1ZoRFRaK0w4V29qOVY2Mjc3dVVqQT09"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary whitespace-nowrap"
                >
                  Join on Zoom
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
